import React, { useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

import type { BranchData } from '@/utilities/types/table'
import type { ProvinceData } from '@/utilities/types/table'

interface ProvinceChipsDisplayProps {
  branches: BranchData[]
}

export const ProvinceChipsDisplay: React.FC<ProvinceChipsDisplayProps> = ({
  branches,
}) => {
  const [selectedBranch, setSelectedBranch] = useState<BranchData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false)

  const handleChipClick = (branch: BranchData) => {
    setSelectedBranch(branch)
    setIsModalOpen(true)
  }

  const handleViewAllClick = () => {
    setIsViewAllModalOpen(true)
  }

  const sortedBranches = [...branches].sort((a, b) =>
    a.name.localeCompare(b.name),
  )

  const renderChips = () => {
    if (sortedBranches.length <= 1) {
      return sortedBranches.map((branch) => (
        <Chip
          key={branch.id}
          label={branch.name}
          onClick={() => handleChipClick(branch)}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.08)',
            },
            margin: '2px',
          }}
          variant="outlined"
          color="primary"
        />
      ))
    }

    return (
      <Chip
        label={`View All Branches (${sortedBranches.length})`}
        onClick={handleViewAllClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.08)',
          },
        }}
        variant="outlined"
        color="primary"
      />
    )
  }

  if (!sortedBranches || sortedBranches.length === 0) {
    return <span>-</span>
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {renderChips()}
      </Stack>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedBranch && (
          <>
            <DialogTitle
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {sortedBranches.length > 2 && (
                  <Button
                    onClick={() => {
                      setIsModalOpen(false)
                      setIsViewAllModalOpen(true)
                    }}
                    sx={{
                      minWidth: 'auto',
                      textTransform: 'none',
                      p: 1,
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                <Typography variant="h6">Branches Details</Typography>
              </Box>

              <IconButton onClick={() => setIsModalOpen(false)}>
                <CloseIcon
                  sx={{
                    color: '#505470',
                    width: 20,
                    height: 20,
                  }}
                />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {selectedBranch.dealerName}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Branch Name:</Typography>
                  <Typography>{selectedBranch.name}</Typography>
                </Box>
              </Paper>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={isViewAllModalOpen}
        onClose={() => setIsViewAllModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">All Branches</Typography>

          <IconButton
            onClick={() => setIsViewAllModalOpen(false)}
            sx={{ width: 20, height: 20 }}
          >
            <CloseIcon
              sx={{
                color: '#505470',
              }}
            />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            {sortedBranches.map((branch) => (
              <Paper
                key={branch.id}
                elevation={1}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.04)',
                  },
                }}
                onClick={() => {
                  setIsViewAllModalOpen(false)
                  handleChipClick(branch)
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {branch.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {branch.dealerName}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
